import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import Breadcrums from "../../../common/breadcrumbs";
import { SWAL_SETTINGS } from "../../../../../utils/Constants";
import { formateDateWithMonth, getUser, getLocalKey, hasP } from "../../../../../utils/commonfunction";
import * as agreementService from "../../../services/agreements.service";
import NativeDocxViewer from "./NativeDocxViewer";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { renderAsync } from "docx-preview";
import { Modal, Button, Badge, Nav } from "rsuite";

const AgreementList = () => {
    const [activeTab, setActiveTab] = useState("templates");
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [loadingDocuments, setLoadingDocuments] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [previewDoc] = useState(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedTemplateForView, setSelectedTemplateForView] = useState(null);
    const [showTemplateViewModal, setShowTemplateViewModal] = useState(false);
    const [targetTemplateForUpload, setTargetTemplateForUpload] = useState(null);

    const [selectedTemplates, setSelectedTemplates] = useState([]);
    const [selectedDocuments, setSelectedDocuments] = useState([]);

    const handleToggleTemplate = (id) => {
        setSelectedTemplates(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const handleSelectAllTemplates = () => {
        const visibleTemplates = templates.slice(0, visibleCount);
        if (selectedTemplates.length === visibleTemplates.length) {
            setSelectedTemplates([]);
        } else {
            setSelectedTemplates(visibleTemplates.map(t => t.id));
        }
    };

    const handleBulkDeleteTemplates = () => {
        if (selectedTemplates.length === 0) return;
        Swal.fire({
            title: "Are you sure?",
            text: `Do you want to delete ${selectedTemplates.length} selected agreement template(s)?`,
            icon: "warning",
            showCancelButton: true,
            showConfirmButton: true,
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, delete selected!",
            cancelButtonText: "Cancel"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await agreementService.bulkDeleteTemplates(selectedTemplates);
                    if (res && res.success) {
                        Swal.fire("Deleted!", "Selected agreement templates have been deleted.", "success");
                        setSelectedTemplates([]);
                        fetchTemplates();
                    } else {
                        Swal.fire("Error", res?.message || "Failed to delete selected templates.", "error");
                    }
                } catch (err) {
                    Swal.fire("Error", "Failed to bulk delete agreement templates.", "error");
                }
            }
        });
    };

    const handleToggleDocument = (id) => {
        setSelectedDocuments(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const handleSelectAllDocuments = () => {
        const visibleDocs = documents.slice(0, visibleCount);
        if (selectedDocuments.length === visibleDocs.length) {
            setSelectedDocuments([]);
        } else {
            setSelectedDocuments(visibleDocs.map(d => d.id));
        }
    };

    const handleDeleteClubDocument = (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "Do you want to delete this agreement document record?",
            icon: "warning",
            showCancelButton: true,
            showConfirmButton: true,
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "Cancel"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await agreementService.deleteClubDocument(id);
                    if (res && res.success) {
                        Swal.fire("Deleted!", "Agreement document record deleted.", "success");
                        fetchDocuments();
                    } else {
                        Swal.fire("Error", res?.message || "Failed to delete document record.", "error");
                    }
                } catch (err) {
                    Swal.fire("Error", "Failed to delete agreement document.", "error");
                }
            }
        });
    };

    const handleBulkDeleteDocuments = () => {
        if (selectedDocuments.length === 0) return;
        Swal.fire({
            title: "Are you sure?",
            text: `Do you want to delete ${selectedDocuments.length} selected agreement document(s)?`,
            icon: "warning",
            showCancelButton: true,
            showConfirmButton: true,
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, delete selected!",
            cancelButtonText: "Cancel"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await agreementService.bulkDeleteClubDocuments(selectedDocuments);
                    if (res && res.success) {
                        Swal.fire("Deleted!", "Selected agreement documents have been deleted.", "success");
                        setSelectedDocuments([]);
                        fetchDocuments();
                    } else {
                        Swal.fire("Error", res?.message || "Failed to delete selected documents.", "error");
                    }
                } catch (err) {
                    Swal.fire("Error", "Failed to bulk delete agreement documents.", "error");
                }
            }
        });
    };

    const [visibleCount, setVisibleCount] = useState(10);
    const observerTarget = useRef(null);

    useEffect(() => {
        setVisibleCount(10);
    }, [activeTab]);

    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 200) {
                if (activeTab === "documents") {
                    setVisibleCount((prev) => (prev < documents.length ? prev + 10 : prev));
                } else {
                    setVisibleCount((prev) => (prev < templates.length ? prev + 10 : prev));
                }
            }
        };

        window.addEventListener("scroll", handleScroll);

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0] && entries[0].isIntersecting) {
                    if (activeTab === "documents") {
                        setVisibleCount((prev) => (prev < documents.length ? prev + 10 : prev));
                    } else {
                        setVisibleCount((prev) => (prev < templates.length ? prev + 10 : prev));
                    }
                }
            },
            { threshold: 0.1, rootMargin: "150px" }
        );

        const currentTarget = observerTarget.current;
        if (currentTarget) {
            observer.observe(currentTarget);
        }

        return () => {
            window.removeEventListener("scroll", handleScroll);
            if (currentTarget) {
                observer.unobserve(currentTarget);
            }
        };
    }, [activeTab, documents.length, templates.length]);

    const breadcrumbs = [
        { title: "Dashboard", url: "/admin/dashboard" },
        { title: "CMS", url: "" },
        { title: "Agreement Management", url: "" },
    ];

    const fetchTemplates = async () => {
        setLoadingTemplates(true);
        try {
            const res = await agreementService.listTemplates();
            if (res && res.success) {
                setTemplates(res.data?.list || []);
            }
        } catch (err) {
            console.error("Failed to load agreement templates:", err);
        } finally {
            setLoadingTemplates(false);
        }
    };

    const fetchDocuments = async () => {
        setLoadingDocuments(true);
        try {
            const res = await agreementService.listClubDocuments();
            if (res && res.success) {
                setDocuments(res.data?.list || []);
            }
        } catch (err) {
            console.error("Failed to load club documents:", err);
        } finally {
            setLoadingDocuments(false);
        }
    };

    useEffect(() => {
        if (activeTab === "templates") {
            // Fetch Tab 1 data and also pre-fetch Tab 2 data in background
            fetchTemplates();
            fetchDocuments();
        } else {
            fetchDocuments();
        }
    }, [activeTab]);

    const handleDeleteTemplate = (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "Do you want to delete this agreement template?",
            icon: "warning",
            showCancelButton: true,
            showConfirmButton: true,
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "Cancel"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await agreementService.deleteTemplate(id);
                    if (res && res.success) {
                        Swal.fire("Deleted!", "Agreement template has been deleted.", "success");
                        fetchTemplates();
                    }
                } catch (err) {
                    Swal.fire("Error", "Failed to delete agreement template.", "error");
                }
            }
        });
    };

    const handleViewDocument = (doc) => {
        // Store clubId explicitly so NativeDocxViewer fetches /render-docx/:clubId
        // (which loads the club's user data, not the template). viewType distinguishes
        // club doc previews from template previews in the shared modal.
        setSelectedTemplateForView({
            clubId: doc.clubId || doc.id,
            id: null,   // not a template preview — no templateId
            viewType: "club",
            title: `${doc.placeholdersData?.clubName || doc.clubName || "Club"} Agreement`
        });
        setShowTemplateViewModal(true);
    };

    const handleUploadTemplate = async (e) => {
        if (e) e.preventDefault();
        if (!selectedFile) {
            Swal.fire("Validation Error", "Please select a Word (.docx) file to upload.", "warning");
            return;
        }

        const ext = selectedFile.name ? selectedFile.name.split('.').pop().toLowerCase() : "";
        if (ext !== "docx") {
            Swal.fire("Validation Error", "Invalid file format. Only Word (.docx) files are allowed.", "warning");
            return;
        }

        const formData = new FormData();
        formData.append("templateFile", selectedFile);
        if (targetTemplateForUpload && targetTemplateForUpload.id) {
            formData.append("templateId", targetTemplateForUpload.id);
        }

        setUploadingFile(true);
        try {
            const res = await agreementService.uploadTemplateFile(formData);
            if (res && res.success) {
                Swal.fire({
                    icon: "success",
                    title: "Template Replaced!",
                    text: res.message || "Master agreement template file updated successfully.",
                    timer: 2500,
                    showConfirmButton: false,
                    ...SWAL_SETTINGS
                });
                setShowUploadModal(false);
                setSelectedFile(null);
                fetchTemplates();
            } else {
                Swal.fire("Upload Failed", res?.message || "Failed to upload template.", "error");
            }
        } catch (err) {
            Swal.fire("Upload Error", err?.message || "An error occurred while uploading file.", "error");
        } finally {
            setUploadingFile(false);
        }
    };

    const handleDownloadClubDocument = async (doc) => {
        let tempContainer = null;
        try {
            Swal.fire({
                title: "Generating PDF...",
                text: "Converting agreement document to multi-page PDF...",
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const targetClubId = doc.clubId || doc.id;
            const apiBase = process.env.REACT_APP_API_URL || "http://localhost:7000";
            let cleanBase = apiBase.endsWith("/") ? apiBase.slice(0, -1) : apiBase;
            if (!cleanBase.includes("/webservice/api/v1")) {
                cleanBase = `${cleanBase}/webservice/api/v1`;
            }

            let token = "";
            try {
                const rolesToTry = ["super_admin", "sub_admin", "club_admin"];
                for (const r of rolesToTry) {
                    const uData = getUser(r);
                    if (uData && uData.token) { token = uData.token; break; }
                    const raw = getLocalKey(r) || localStorage.getItem(r);
                    if (raw) {
                        try {
                            const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                            if (parsed && parsed.token) { token = parsed.token; break; }
                        } catch (e) {}
                    }
                }
            } catch (e) {}

            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            // 1. Fetch 1:1 designed DOCX blob for this club
            const downloadUrl = `${cleanBase}/admin/cms/agreements/render-docx/${targetClubId}`;
            const response = await fetch(downloadUrl, { headers });

            if (!response.ok) throw new Error("Failed to fetch document template.");
            const docxBlob = await response.blob();

            // 2. Render DOCX into offscreen DOM container using docx-preview
            tempContainer = document.createElement("div");
            tempContainer.style.position = "fixed";
            tempContainer.style.left = "-9999px";
            tempContainer.style.top = "0px";
            tempContainer.style.width = "850px";
            tempContainer.style.background = "#ffffff";
            document.body.appendChild(tempContainer);

            await renderAsync(docxBlob, tempContainer, null, {
                className: "docx-container",
                inWrapper: true,
                ignoreWidth: false,
                ignoreHeight: false,
                experimental: true,
                useBase64URL: true
            });

            await new Promise(r => setTimeout(r, 600));

            // 3. Convert each page section into individual A4 PDF pages
            const pdf = new jsPDF("p", "pt", "a4");
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();

            const pageSections = tempContainer.querySelectorAll("section.docx");

            if (pageSections && pageSections.length > 0) {
                for (let i = 0; i < pageSections.length; i++) {
                    const sec = pageSections[i];
                    const canvas = await html2canvas(sec, {
                        scale: 2,
                        useCORS: true,
                        logging: false,
                        backgroundColor: "#ffffff"
                    });
                    const imgData = canvas.toDataURL("image/jpeg", 0.95);
                    if (i > 0) pdf.addPage();
                    pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);
                }
            } else {
                const canvas = await html2canvas(tempContainer, {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    backgroundColor: "#ffffff"
                });

                const imgWidth = canvas.width;
                const imgHeight = canvas.height;
                const pageCanvasHeight = Math.floor((imgWidth * pdfHeight) / pdfWidth);

                let heightLeft = imgHeight;
                let position = 0;

                const pageCanvas = document.createElement("canvas");
                const ctx = pageCanvas.getContext("2d");
                pageCanvas.width = imgWidth;

                let pageIdx = 0;
                while (heightLeft > 0) {
                    const chunkHeight = Math.min(heightLeft, pageCanvasHeight);
                    pageCanvas.height = pageCanvasHeight;

                    ctx.fillStyle = "#ffffff";
                    ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
                    ctx.drawImage(canvas, 0, position, imgWidth, chunkHeight, 0, 0, imgWidth, chunkHeight);

                    const imgData = pageCanvas.toDataURL("image/jpeg", 0.95);

                    if (pageIdx > 0) pdf.addPage();
                    pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);

                    position += chunkHeight;
                    heightLeft -= chunkHeight;
                    pageIdx++;
                }
            }

            const clubNameClean = (doc.placeholdersData?.clubName || doc.clubName || "Club").replace(/[^a-zA-Z0-9]/g, "_");
            pdf.save(`Commercial-Agreement-${clubNameClean}.pdf`);
            Swal.close();
        } catch (err) {
            Swal.close();
            console.error("[AgreementList] Download PDF Error:", err);
            Swal.fire({ icon: "error", text: err.message || "Failed to download document.", ...SWAL_SETTINGS });
        } finally {
            if (tempContainer && document.body.contains(tempContainer)) {
                document.body.removeChild(tempContainer);
            }
        }
    };

    const handleDownloadTemplateFile = async (tpl, format = "docx") => {
        try {
            Swal.fire({
                title: "Downloading Template...",
                text: `Preparing ${format.toUpperCase()} template document for download...`,
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            // Use signed downloadUrl if available (preferred), else fall back to s3Url
            if (tpl && (tpl.downloadUrl || tpl.s3Url) && format === "docx") {
                const directUrl = tpl.downloadUrl || tpl.s3Url;
                const a = document.createElement("a");
                a.href = directUrl;
                a.download = `${(tpl.title || tpl.affiliationType || "Agreement-Template").replace(/[^a-zA-Z0-9]/g, "_")}.docx`;
                a.target = "_blank";
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                Swal.close();
                return;
            }

            const apiBase = process.env.REACT_APP_API_URL || "http://localhost:7000";
            let cleanBase = apiBase.endsWith("/") ? apiBase.slice(0, -1) : apiBase;
            if (!cleanBase.includes("/webservice/api/v1")) {
                cleanBase = `${cleanBase}/webservice/api/v1`;
            }

            let token = "";
            try {
                const rolesToTry = ["super_admin", "sub_admin", "club_admin"];
                for (const r of rolesToTry) {
                    const uData = getUser(r);
                    if (uData && uData.token) { token = uData.token; break; }
                    const raw = getLocalKey(r) || localStorage.getItem(r);
                    if (raw) {
                        try {
                            const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                            if (parsed && parsed.token) { token = parsed.token; break; }
                        } catch (e) {}
                    }
                }
            } catch (e) {}

            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const templateId = tpl?.id || tpl?.affiliationType || "";
            const downloadUrl = `${cleanBase}/admin/cms/agreements/render-${format}${templateId ? `?templateId=${templateId}` : ""}`;

            const response = await fetch(downloadUrl, { headers });
            if (!response.ok) throw new Error("Failed to download template file.");
            const blob = await response.blob();

            const fileName = `${(tpl?.title || tpl?.affiliationType || "Commercial-Agreement-Template").replace(/[^a-zA-Z0-9]/g, "_")}.${format}`;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            Swal.close();
        } catch (err) {
            Swal.close();
            console.error("[AgreementList] Download Template Error:", err);
            Swal.fire({ icon: "error", text: err.message || "Failed to download template file.", ...SWAL_SETTINGS });
        }
    };

    return (
        <div className="main-content header-dark">
            <div className="container-fluid">
                <Breadcrums data={breadcrumbs} />

                <div className="row row-sm">
                    <div className="col-lg-12">
                        <div className="card custom-card">
                            <div className="card-header border-bottom-0 d-flex justify-content-between align-items-center">
                                <div>
                                    <h4 className="card-title font-weight-bold mb-1">Commercial Agreement Management</h4>
                                    <p className="tx-12 tx-gray-500 mb-0">
                                        Configure agreement templates, manage dynamic placeholders, upload docx templates, and view signed club documents.
                                    </p>
                                </div>
                                {activeTab === "templates" && selectedTemplates.length > 0 && (
                                    <button
                                        className="btn btn-danger btn-sm font-weight-bold ms-auto d-flex align-items-center gap-1"
                                        onClick={handleBulkDeleteTemplates}
                                    >
                                        <i className="fa fa-trash me-1"></i> Delete Selected ({selectedTemplates.length})
                                    </button>
                                )}
                                {activeTab === "documents" && selectedDocuments.length > 0 && (
                                    <button
                                        className="btn btn-danger btn-sm font-weight-bold ms-auto d-flex align-items-center gap-1"
                                        onClick={handleBulkDeleteDocuments}
                                    >
                                        <i className="fa fa-trash me-1"></i> Delete Selected ({selectedDocuments.length})
                                    </button>
                                )}
                            </div>

                            <div className="card-body">
                                <Nav
                                    appearance="tabs"
                                    activeKey={activeTab}
                                    onSelect={setActiveTab}
                                    style={{ marginBottom: "20px" }}
                                >
                                    <Nav.Item eventKey="templates" icon={<i className="fa fa-file-text me-2"></i>}>
                                        Agreement Templates ({templates.length})
                                    </Nav.Item>
                                    <Nav.Item eventKey="documents" icon={<i className="fa fa-folder-open me-2"></i>}>
                                        Club Documents History ({documents.length})
                                    </Nav.Item>
                                </Nav>

                                {activeTab === "templates" ? (
                                    <div className="table-responsive">
                                        <table className="table table-bordered text-nowrap border-top mb-0 align-middle">
                                            <thead className="table-light">
                                                <tr>
                                                    <th style={{ width: 40, textAlign: "center" }}>
                                                        <input
                                                            type="checkbox"
                                                            className="cp"
                                                            style={{
                                                                width: "18px",
                                                                height: "18px",
                                                                cursor: "pointer",
                                                                accentColor: "#007d7f",
                                                                WebkitAppearance: "checkbox",
                                                                appearance: "checkbox",
                                                                display: "inline-block",
                                                                verticalAlign: "middle"
                                                            }}
                                                            checked={
                                                                templates.slice(0, visibleCount).length > 0 &&
                                                                selectedTemplates.length === templates.slice(0, visibleCount).length
                                                            }
                                                            onChange={handleSelectAllTemplates}
                                                        />
                                                    </th>
                                                    <th style={{ width: 60, textAlign: "center" }}>S.No</th>
                                                    <th>Template Title</th>
                                                    <th>Affiliation Type</th>
                                                    <th>Version</th>
                                                    <th>Status</th>
                                                    <th>Created Date</th>
                                                    <th className="text-center" style={{ position: "sticky", right: 0, backgroundColor: "#f8f9fa", zIndex: 2, boxShadow: "-2px 0 5px -2px rgba(0,0,0,0.15)" }}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loadingTemplates ? (
                                                    <tr>
                                                        <td colSpan="8" className="text-center py-4">
                                                            <div className="spinner-border text-primary" role="status">
                                                                <span className="sr-only">Loading...</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : templates.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="8" className="text-center py-4 text-muted">
                                                            No agreement templates found. Click "Create Agreement Template" to add one.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    templates.slice(0, visibleCount).map((tpl, idx) => (
                                                        <tr key={tpl.id}>
                                                            <td style={{ textAlign: "center" }}>
                                                                <input
                                                                    type="checkbox"
                                                                    className="cp"
                                                                    style={{
                                                                        width: "18px",
                                                                        height: "18px",
                                                                        cursor: "pointer",
                                                                        accentColor: "#007d7f",
                                                                        WebkitAppearance: "checkbox",
                                                                        appearance: "checkbox",
                                                                        display: "inline-block",
                                                                        verticalAlign: "middle"
                                                                    }}
                                                                    checked={selectedTemplates.includes(tpl.id)}
                                                                    onChange={() => handleToggleTemplate(tpl.id)}
                                                                />
                                                            </td>
                                                            <td style={{ textAlign: "center" }}>{idx + 1}</td>
                                                            <td>
                                                                <strong>{tpl.title}</strong>
                                                                {tpl.description && (
                                                                    <div className="tx-12 text-muted">{tpl.description}</div>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <Badge
                                                                    style={{
                                                                        backgroundColor:
                                                                            tpl.affiliationType === "Founder"
                                                                                ? "#007d7f"
                                                                                : tpl.affiliationType === "Partner"
                                                                                ? "#ff9f43"
                                                                                : "#6c757d",
                                                                        color: "#fff",
                                                                        padding: "4px 10px",
                                                                        fontSize: "12px"
                                                                    }}
                                                                >
                                                                    {tpl.affiliationType || "Founder"}
                                                                </Badge>
                                                            </td>
                                                            <td>
                                                                <span className="badge badge-outline-secondary">v{tpl.version || 1}</span>
                                                            </td>
                                                            <td>
                                                                {tpl.status === "ACTIVE" ? (
                                                                    <span className="badge bg-success">ACTIVE</span>
                                                                ) : (
                                                                    <span className="badge bg-secondary">INACTIVE</span>
                                                                )}
                                                            </td>
                                                            <td>{tpl.createdAt ? formateDateWithMonth(tpl.createdAt) : "N/A"}</td>
                                                            <td className="text-center" style={{ position: "sticky", right: 0, backgroundColor: "#ffffff", zIndex: 1, boxShadow: "-2px 0 5px -2px rgba(0,0,0,0.15)" }}>
                                                                {hasP("agreements-view") && (
                                                                    <button
                                                                        className="btn btn-sm text-white me-1 mb-1"
                                                                        style={{ backgroundColor: "#007d7f", borderColor: "#007d7f" }}
                                                                        title="View 1:1 Rendered Agreement with Mapped Variables"
                                                                        onClick={() => {
                                                                            setSelectedTemplateForView({ ...tpl, viewType: "template" });
                                                                            setShowTemplateViewModal(true);
                                                                        }}
                                                                    >
                                                                        <i className="fa fa-eye me-1"></i> Preview Agreement
                                                                    </button>
                                                                )}
                                                                {hasP("agreements-view") && (
                                                                    <button
                                                                        className="btn btn-sm btn-success text-white me-1 mb-1"
                                                                        title="Download Agreement Template (.docx)"
                                                                        onClick={() => handleDownloadTemplateFile(tpl, "docx")}
                                                                    >
                                                                        <i className="fa fa-download me-1"></i> Download
                                                                    </button>
                                                                )}
                                                                {(hasP("agreements-edit") || hasP("agreements-add")) && (
                                                                    <button
                                                                        className="btn btn-sm btn-outline-primary me-1 mb-1"
                                                                        title="Upload Custom File (.docx) for this Template"
                                                                        onClick={() => {
                                                                            setTargetTemplateForUpload(tpl);
                                                                            setShowUploadModal(true);
                                                                        }}
                                                                    >
                                                                        <i className="fa fa-upload me-1"></i> Upload
                                                                    </button>
                                                                )}
                                                                {hasP("agreements-delete") && (
                                                                    <button
                                                                        className="btn btn-sm btn-danger mb-1 text-white"
                                                                        title="Delete Template"
                                                                        onClick={() => handleDeleteTemplate(tpl.id)}
                                                                    >
                                                                        <i className="fa fa-trash"></i>
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>

                                        {templates.length > 0 && (
                                            <div ref={observerTarget} className="text-center py-3 border-top bg-light">
                                                {visibleCount < templates.length ? (
                                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                                        <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                                                        <span className="tx-13 text-muted">
                                                            Showing {Math.min(visibleCount, templates.length)} of {templates.length} templates. Scroll down to load more...
                                                        </span>
                                                        <button
                                                            className="btn btn-xs btn-outline-primary ms-2"
                                                            onClick={() => setVisibleCount(prev => prev + 10)}
                                                        >
                                                            Load More
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <span className="tx-12 text-muted">Showing all {templates.length} templates</span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-bordered text-nowrap border-top mb-0 align-middle">
                                            <thead className="table-light">
                                                <tr>
                                                    <th style={{ width: 40, textAlign: "center" }}>
                                                        <input
                                                            type="checkbox"
                                                            className="cp"
                                                            style={{
                                                                width: "18px",
                                                                height: "18px",
                                                                cursor: "pointer",
                                                                accentColor: "#007d7f",
                                                                WebkitAppearance: "checkbox",
                                                                appearance: "checkbox",
                                                                display: "inline-block",
                                                                verticalAlign: "middle"
                                                            }}
                                                            checked={
                                                                documents.slice(0, visibleCount).length > 0 &&
                                                                selectedDocuments.length === documents.slice(0, visibleCount).length
                                                            }
                                                            onChange={handleSelectAllDocuments}
                                                        />
                                                    </th>
                                                    <th style={{ width: 60, textAlign: "center" }}>S.No</th>
                                                    <th>Document Title</th>
                                                    <th>Club Details</th>
                                                    <th>Affiliation Type</th>
                                                    <th>Version</th>
                                                    <th>Status</th>
                                                    <th>Date Signed</th>
                                                    <th className="text-center" style={{ position: "sticky", right: 0, backgroundColor: "#f8f9fa", zIndex: 2, boxShadow: "-2px 0 5px -2px rgba(0,0,0,0.15)" }}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {loadingDocuments ? (
                                                    <tr>
                                                        <td colSpan="9" className="text-center py-4">
                                                            <div className="spinner-border text-primary" role="status">
                                                                <span className="sr-only">Loading...</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : documents.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="9" className="text-center py-4 text-muted">
                                                            No generated agreement documents recorded yet.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    documents.slice(0, visibleCount).map((doc, idx) => (
                                                        <tr key={doc.id}>
                                                            <td style={{ textAlign: "center" }}>
                                                                <input
                                                                    type="checkbox"
                                                                    className="cp"
                                                                    style={{
                                                                        width: "18px",
                                                                        height: "18px",
                                                                        cursor: "pointer",
                                                                        accentColor: "#007d7f",
                                                                        WebkitAppearance: "checkbox",
                                                                        appearance: "checkbox",
                                                                        display: "inline-block",
                                                                        verticalAlign: "middle"
                                                                    }}
                                                                    checked={selectedDocuments.includes(doc.id)}
                                                                    onChange={() => handleToggleDocument(doc.id)}
                                                                />
                                                            </td>
                                                            <td style={{ textAlign: "center" }}>{idx + 1}</td>
                                                            <td>
                                                                <strong>{doc.title || "Commercial Agreement"}</strong>
                                                            </td>
                                                            <td>
                                                                <strong>
                                                                    {(!doc.clubName || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(doc.clubName))
                                                                        ? (doc.placeholdersData?.clubName && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(doc.placeholdersData.clubName)
                                                                            ? doc.placeholdersData.clubName
                                                                            : doc.clubEmail || doc.placeholdersData?.contactEmail || doc.clubId)
                                                                        : doc.clubName}
                                                                </strong>
                                                                {(doc.placeholdersData?.contactEmail || doc.clubEmail) && (
                                                                    <div className="tx-12 text-muted">{doc.placeholdersData?.contactEmail || doc.clubEmail}</div>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <Badge
                                                                    style={{
                                                                        backgroundColor:
                                                                            doc.affiliationType === "Founder"
                                                                                ? "#007d7f"
                                                                                : doc.affiliationType === "Partner"
                                                                                ? "#ff9f43"
                                                                                : "#6c757d",
                                                                        color: "#fff",
                                                                        padding: "4px 10px",
                                                                        fontSize: "12px"
                                                                    }}
                                                                >
                                                                    {doc.affiliationType || "Founder"}
                                                                </Badge>
                                                            </td>
                                                            <td>v{doc.templateVersion || 1}</td>
                                                            <td>
                                                                <span className="badge bg-success">{doc.status || "SIGNED"}</span>
                                                            </td>
                                                            <td>{doc.signedAt || doc.createdAt ? formateDateWithMonth(doc.signedAt || doc.createdAt) : "N/A"}</td>
                                                            <td className="text-center" style={{ position: "sticky", right: 0, backgroundColor: "#ffffff", zIndex: 1, boxShadow: "-2px 0 5px -2px rgba(0,0,0,0.15)" }}>
                                                                <button
                                                                    className="btn btn-sm btn-primary text-white me-2"
                                                                    onClick={() => handleViewDocument(doc)}
                                                                >
                                                                    <i className="fa fa-eye me-1"></i> View
                                                                </button>
                                                                <button
                                                                    className="btn btn-sm btn-success text-white me-2"
                                                                    onClick={() => handleDownloadClubDocument(doc)}
                                                                >
                                                                    <i className="fa fa-download me-1"></i> Download
                                                                </button>
                                                                <button
                                                                    className="btn btn-sm btn-danger text-white"
                                                                    onClick={() => handleDeleteClubDocument(doc.id)}
                                                                    title="Delete Document"
                                                                >
                                                                    <i className="fa fa-trash me-1"></i> Delete
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>

                                        {documents.length > 0 && (
                                            <div ref={observerTarget} className="text-center py-3 border-top bg-light">
                                                {visibleCount < documents.length ? (
                                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                                        <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                                                        <span className="tx-13 text-muted">
                                                            Showing {Math.min(visibleCount, documents.length)} of {documents.length} records. Scroll down to load more...
                                                        </span>
                                                        <button
                                                            className="btn btn-xs btn-outline-primary ms-2"
                                                            onClick={() => setVisibleCount(prev => prev + 10)}
                                                        >
                                                            Load More
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <span className="tx-12 text-muted">Showing all {documents.length} records</span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Preview Document Modal */}
                <Modal open={showPreviewModal} onClose={() => setShowPreviewModal(false)} size="lg" className="responsive-agreement-modal">
                    <Modal.Header>
                        <Modal.Title className="font-weight-bold tx-14 tx-sm-16">
                            <i className="fa fa-file-pdf-o text-danger me-2"></i>
                            {previewDoc?.title || "Agreement Document Preview"}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="p-2 p-sm-3">
                        {previewDoc && (
                            <div>
                                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-3 p-2 p-sm-3 bg-light rounded gap-2">
                                    <div>
                                        <h6 className="mb-1 font-weight-bold">{previewDoc.placeholdersData?.clubName || previewDoc.clubName || "Club Document"}</h6>
                                        <div className="tx-12 text-muted">
                                            Affiliation: <strong>{previewDoc.affiliationType || "Founder"}</strong> | Version: <strong>v{previewDoc.templateVersion || 1}</strong>
                                        </div>
                                    </div>
                                    <div>
                                        <span className="badge bg-success">{previewDoc.status || "SIGNED"}</span>
                                    </div>
                                </div>

                                {previewDoc.downloadUrl || previewDoc.s3Url ? (
                                    <div className="pdf-viewer-container" style={{ width: "100%", borderRadius: "8px", overflow: "hidden", border: "1px solid #e2e8f0" }}>
                                        <iframe
                                            src={previewDoc.downloadUrl || previewDoc.s3Url}
                                            title="Agreement PDF"
                                            width="100%"
                                            style={{ width: "100%", height: "60vh", minHeight: "350px", maxHeight: "600px", border: "none", display: "block" }}
                                        />
                                    </div>
                                ) : (
                                    <div className="alert alert-warning">No document file available for preview.</div>
                                )}
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                        {previewDoc?.downloadUrl && (
                            <a
                                href={previewDoc.downloadUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-success btn-sm text-white"
                                download
                            >
                                <i className="fa fa-download me-1"></i> Download PDF
                            </a>
                        )}
                        <Button onClick={() => setShowPreviewModal(false)} appearance="subtle">
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Upload Master Template Modal */}
                <Modal open={showUploadModal} onClose={() => setShowUploadModal(false)} size="md" className="responsive-agreement-modal">
                    <Modal.Header>
                        <Modal.Title className="font-weight-bold tx-14 tx-sm-16">
                            <i className="fa fa-file-word text-primary me-2"></i> Upload & Replace Master Agreement Template
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="p-2 p-sm-3">
                        <form onSubmit={handleUploadTemplate}>
                            <div className="p-2 p-sm-3">
                                <p className="tx-12 tx-sm-13 text-muted mb-3">
                                    Select a Word (<strong>.docx</strong>) document file to replace the master commercial agreement template. Dynamic placeholders (e.g. <code>&#123;clubName&#125;</code>, <code>&#123;address&#125;</code>, <code>&#123;agreementDate&#125;</code>) will automatically map to database values during execution.
                                </p>
                                <div className="mb-3">
                                    <label className="form-label font-weight-bold tx-12 tx-sm-13">
                                        Upload Master Template File (.docx) <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        accept=".docx"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                const ext = file.name.split('.').pop().toLowerCase();
                                                if (ext !== "docx") {
                                                    Swal.fire("Validation Error", "Only Word (.docx) files are supported.", "warning");
                                                    e.target.value = "";
                                                    setSelectedFile(null);
                                                    return;
                                                }
                                            }
                                            setSelectedFile(file);
                                        }}
                                        required
                                    />
                                    {selectedFile && (
                                        <div className="mt-2 text-success tx-12">
                                            <i className="fa fa-check-circle me-1"></i> Ready to upload: <strong>{selectedFile.name}</strong> ({(selectedFile.size / 1024).toFixed(1)} KB)
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="d-flex justify-content-end gap-2 px-2 px-sm-3 pb-2 pb-sm-3">
                                <Button onClick={() => setShowUploadModal(false)} appearance="subtle">
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    appearance="primary"
                                    loading={uploadingFile}
                                    style={{ backgroundColor: "#007d7f", borderColor: "#007d7f" }}
                                >
                                    <i className="fa fa-cloud-upload me-1"></i> Upload & Replace Template
                                </Button>
                            </div>
                        </form>
                    </Modal.Body>
                </Modal>

                {/* Rendered Template View Modal */}
                <Modal open={showTemplateViewModal} onClose={() => setShowTemplateViewModal(false)} size="lg" className="responsive-agreement-modal">
                    <Modal.Header>
                        <Modal.Title className="font-weight-bold d-flex flex-wrap justify-content-between align-items-center w-100 pe-2 pe-sm-4 tx-14 tx-sm-16">
                            <span>
                                <i className="fa fa-eye text-primary me-2"></i> Agreement Preview ({selectedTemplateForView?.title || "Master Template"})
                            </span>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="p-2 p-sm-3">
                        <div>
                            <div className="alert alert-info d-flex align-items-start align-items-sm-center mb-3 p-2 p-sm-3">
                                <i className="fa fa-info-circle fa-lg me-2 mt-1 mt-sm-0 flex-shrink-0"></i>
                                <span className="tx-12 tx-sm-13">
                                    Rendering visual match Word (.docx) document template with dynamic placeholders (e.g. <code>&#123;clubName&#125;</code>, <code>&#123;address&#125;</code>, <code>&#123;agreementDate&#125;</code>, <code>&#123;ratePerPlayer&#125;</code>) automatically populated from the database.
                                </span>
                            </div>
                            <NativeDocxViewer
                                templateId={selectedTemplateForView?.viewType === "club" ? null : selectedTemplateForView?.id}
                                clubId={selectedTemplateForView?.viewType === "club" ? selectedTemplateForView?.clubId : null}
                            />
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="d-flex justify-content-end align-items-center">
                        <Button onClick={() => setShowTemplateViewModal(false)} appearance="subtle">
                            Close Preview
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    );
};

export default AgreementList;
